/**
 * Split a string into lines that fit within a specified width and number of lines.
 * If the text exceeds the maximum width or number of lines, it will be truncated with an ellipsis.
 * We do word-level truncation for all lines except the last, where we always do character-level
 * truncation to fit as much text as possible.
 * The function uses a CanvasRenderingContext2D to measure text width.
 * The canvas context font should be set to the desired font style before calling this function.
 */
export function splitTextToLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
  ellipsis = '…',
): string[] {
  if (maxLines <= 0 || maxWidth <= 0 || !text) {
    return []
  }

  const words = text.split(' ')
  const lines: string[] = []
  let currentLine = ''
  let wordIndex = 0

  // Build lines word by word
  while (wordIndex < words.length && lines.length < maxLines) {
    const word = words[wordIndex]
    const testLine = currentLine ? `${currentLine} ${word}` : word
    const isLastPossibleLine = lines.length === maxLines - 1

    // If this is the last possible line, switch to character-level processing
    if (isLastPossibleLine) {
      const remainingText = words.slice(wordIndex).join(' ')
      const finalLine = currentLine ? `${currentLine} ${remainingText}` : remainingText

      const truncatedLine = truncateLineWithCharacters(ctx, finalLine, maxWidth, ellipsis)
      if (truncatedLine) {
        lines.push(truncatedLine)
      }
      break
    }

    // Check if the word fits on current line
    if (ctx.measureText(testLine).width <= maxWidth) {
      currentLine = testLine
      wordIndex++
    } else if (currentLine) {
      // Current line has content, start new line with this word
      lines.push(currentLine)
      currentLine = ''
      // Don't increment wordIndex, process this word again on the new line
    } else {
      // Single word doesn't fit, need to split it across lines
      const splitResult = splitLongWordAcrossLines(
        ctx,
        word,
        maxWidth,
        maxLines - lines.length,
        ellipsis,
      )
      lines.push(...splitResult.lines)

      if (splitResult.truncated) {
        break
      }

      wordIndex++
    }
  }

  // Add remaining current line if we have space and content
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine)
  }

  return lines
}

/**
 * Truncate a line using character-level truncation to fit as much as possible
 */
function truncateLineWithCharacters(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  ellipsis: string,
): string {
  if (ctx.measureText(text).width <= maxWidth) {
    return text
  }

  const ellipsisWidth = ctx.measureText(ellipsis).width

  // If ellipsis alone doesn't fit, return empty
  if (ellipsisWidth > maxWidth) {
    return ''
  }

  // Binary search for the maximum characters that fit with ellipsis
  let low = 0
  let high = text.length
  let bestFit = ''

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const candidate = text.slice(0, mid)
    const candidateWidth = ctx.measureText(candidate).width

    if (candidateWidth + ellipsisWidth <= maxWidth) {
      bestFit = candidate
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  return bestFit ? `${bestFit}${ellipsis}` : ''
}

/**
 * Split a long word across multiple lines using character-level splitting
 */
function splitLongWordAcrossLines(
  ctx: CanvasRenderingContext2D,
  word: string,
  maxWidth: number,
  maxLines: number,
  ellipsis: string,
): { lines: string[]; truncated: boolean } {
  const lines: string[] = []
  let remainingChars = word

  for (let lineIndex = 0; lineIndex < maxLines && remainingChars; lineIndex++) {
    const isLastLine = lineIndex === maxLines - 1

    if (isLastLine) {
      // Last line: use character-level truncation
      const truncatedLine = truncateLineWithCharacters(ctx, remainingChars, maxWidth, ellipsis)
      if (truncatedLine) {
        lines.push(truncatedLine)
      }
      return { lines, truncated: remainingChars.length > 0 }
    } else {
      // Not last line: fit as many characters as possible without ellipsis
      let bestFit = ''

      for (let i = 1; i <= remainingChars.length; i++) {
        const candidate = remainingChars.slice(0, i)
        if (ctx.measureText(candidate).width <= maxWidth) {
          bestFit = candidate
        } else {
          break
        }
      }

      if (bestFit) {
        lines.push(bestFit)
        remainingChars = remainingChars.slice(bestFit.length)
      } else {
        // Can't fit even one character, stop here
        break
      }
    }
  }

  return { lines, truncated: remainingChars.length > 0 }
}
